




















































import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  ref,
  useContext,
  useRoute,
  useRouter,  
  computed
} from '@nuxtjs/composition-api';
import { useCart } from '@wemade-vsf/customer';
import { useSveaOrder, SveaConfig, EstimatedShippingMethod } from '@wemade-vsf/payment-svea';
import { useCustomerPricing } from '@wemade-vsf/catalog/lib/runtime/product/composables/useCustomerPricing';
import SkeletonLoader from 'components/theme/SkeletonLoader.vue';
import CheckoutCart from 'components/checkout/Aside/CheckoutCart.vue';
import CartTotals from 'components/checkout/Aside/CartTotals.vue';
import Comments from 'components/payment-svea/Comments.vue';
import type { SveaOrder, SveaShippingOptionInput } from '@wemade-vsf/magento-api';
import { SfIcon } from '@storefront-ui/vue';
import { ValidationObserver } from 'vee-validate';
import {logToFile} from "~/scripts/logger";

export default defineComponent({
  name: 'Checkout',
  layout: 'checkout',
  components: {
    SkeletonLoader,
    CheckoutCart,
    CartTotals,
    SfIcon,
    ValidationObserver,
    Comments,
    ShippingMethods: () => import('components/payment-svea/ShippingMethods.vue')
  },
  middleware (context) {
    const customerToken = context.app.$vsf.$magento.config.state.getCustomerToken();

    let vsfCustomerCookie = '';
    if (process.client) {
      const match = document.cookie.match(/vsf-customer=([^;]*)/);
      vsfCustomerCookie = match ? match[1] : '';
    } else if (context.req && context.req.headers && context.req.headers.cookie) {
      const match = context.req.headers.cookie.match(/vsf-customer=([^;]*)/);
      vsfCustomerCookie = match ? match[1] : '';
    }

    if (customerToken !== vsfCustomerCookie) {
      context.redirect(302, context.localeRoute({ name: 'checkout-account' }).fullPath);
      return;
    } else if (!customerToken || !vsfCustomerCookie) {
      context.redirect(302, context.localeRoute({ name: 'checkout-account' }).fullPath);
      return;
    }

    const creditCheckApproved = process.client
      ? JSON.parse(localStorage.getItem('credit_check_approved') || 'false')
      : false;

    if (!customerToken) {
      context.redirect(302, context.localeRoute({ name: 'checkout-account' }).fullPath);
      return;
    }

    if (creditCheckApproved === true) {
      context.redirect(302, context.localeRoute({ name: 'checkout-account' }).fullPath);
      return;
    }
  },
  setup () {
    const { app } = useContext();
    const loaded = ref(false);
    const router = useRouter();
    const {
      create,
      update,
      canCheckout,
      confirmShipping,
      error: SveaError,
      loadConfig
    } = useSveaOrder();
    const { load: loadCart, loading: cartLoading, cart } = useCart();

    const { displayPricesIncludingTax: arePricesIncludingTax } = useCustomerPricing();

    const showNewsletter = app.$wm?.paymentSvea?.showNewsletter ?? false;

    const route = useRoute();

    const isThankYou = computed(() => route.value.name.startsWith('checkout-thank-you'));

    const order = ref<SveaOrder | null>(null);
    const sveaConfig = ref<SveaConfig | null>(null);
    const useSveaShipping = computed(() => {
      const shippingActive = sveaConfig.value?.svea_shipping_active ?? false
      return shippingActive || sveaConfig.value?.default_shipping_method === 'svea_nshift_svea_nshift'
    })
    const postscribed = ref(false)

    let expiryTimeout: number|null = null;
    const sveaExpiresAt = ref<number|null>(null);
    const sveaReady = ref<boolean>(false);

    function checkExpiry() {
      if (process.server || !sveaExpiresAt.value) return;
      expiryTimeout = window.setInterval(() => {
        // @ts-ignore
        if (new Date().getTime() >= sveaExpiresAt.value) {
          if (expiryTimeout) clearInterval(expiryTimeout);
          window.location.reload();
        }
      }, 5000);
    }

    const parseSnippet = () => {
      if (process.server || !order.value?.Gui?.Snippet) return;
      const content = order.value.Gui.Snippet;
      const target = document.getElementById('svea-payment');
      if (!target) return;
      app.$postscribe('#svea-payment', content, {
        done: () => {
          postscribed.value = true;
        }
      });
      checkExpiry();
    }

    const pauseCheckout = () => {
      if (!process.server && typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent("sveaCheckout:setIsLoading", { detail: { isLoading: true } }));
      }
    }

    const resumeCheckout = () => {
      if (!process.server && typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent("sveaCheckout:setIsLoading", { detail: { isLoading: false } }));
      }
    }

    const updateOrder = async () => {
      if (!order.value) return
      pauseCheckout();
      let result = await update(order.value.OrderId);
      if (result) {
        order.value = result;
      }
      resumeCheckout()
    }

    const shippingMethod = ref<EstimatedShippingMethod | null>(null)
    const shippingCost = computed(() => {
      if (!shippingMethod.value) return 0;
      return shippingMethod.value.price_excl_tax
    })
    const updateShipping = (method: EstimatedShippingMethod) => {
      shippingMethod.value = method;
      updateOrder()
      loadCart();
    }
    const setShipping = (method: EstimatedShippingMethod) => {
      shippingMethod.value = method;

    }

    const shippingConfirmed = ref(false);
    const onShippingConfirmed = async (data: { detail: SveaShippingOptionInput }) => {
      pauseCheckout();
      await confirmShipping(data.detail);
      let result = await create();
      await loadCart();
      if (!SveaError.value.confirmShipping && !SveaError.value.createOrUpdate && result) {
        order.value = result.order;
      }
      shippingConfirmed.value = true;
      resumeCheckout();
    }

    const onSveaReady = () => {
      sveaReady.value = true;
    }

    onMounted(async() => {
      
      await loadCart({ realCart: true });
      const customerToken = app.$vsf.$magento.config.state.getCustomerToken();
      if (!customerToken) {
        window.location.href = app.localeRoute({ name: 'checkout-account' }).fullPath; 
        return;
      }
      if (!cart.value || !cart.value.items || cart.value.items.length === 0) {
        window.location.href = app.localeRoute({ name: 'home' }).fullPath;
        return;
      }

      sveaConfig.value = await loadConfig();
      if (document) {
        document.addEventListener('checkoutReady', onSveaReady);
      }

      if (canCheckout.value) {
        const result = await create();

        if (result) {
          order.value = result.order;
          sveaExpiresAt.value = result.expiresAt;

            logToFile({
                message: 'SveaCheckout Ready',
                context: {
                    orderValue: order.value,
                    sveaExpiresAt: sveaExpiresAt.value,
                    result: result
                },
                level: 'debug',
                filename: 'svea-debug-log.log'
            });
        }
      }
      if (order.value) {
        parseSnippet();
      }
      loaded.value = true;
      if (document) {
        // @ts-ignore
        document.addEventListener('sveaCheckout:shippingConfirmed', onShippingConfirmed)
      }
    })

    onBeforeUnmount(() => {
      if (document) {
        //@ts-ignore
        document.removeEventListener('sveaCheckout:shippingConfirmed', onShippingConfirmed)
        //@ts-ignore
        document.removeEventListener('checkoutReady', onSveaReady);
      }
      if (expiryTimeout) clearInterval(expiryTimeout);
    })

    return {
      sveaConfig,
      cartId: computed(() => cart.value?.id),
      sveaReady,
      order,
      postscribed,
      loaded,
      cartLoading,
      updateShipping,
      updateOrder,
      setShipping,
      showNewsletter,
      shippingConfirmed,
      shippingMethod,
      useSveaShipping,
      arePricesIncludingTax,
      shippingCost,
      isThankYou
    }
  },
  head() {
    return {
      title: String(this.$t('Checkout'))
    }
  }
})
